<template>
	<div class="pa-3 full-height new-back">
		<div class="d-inline-flex flex-wrap align-center gap-3">
			<q-date-picker
				v-model="filters.startDate"
				@change="getReport"
				label="Start Date" type="month"
				outlined hide-details inner dense
			/>
			<q-date-picker
				v-model="filters.endDate"
				@change="getReport"
				label="End Date" type="month"
				outlined hide-details inner dense
			/>
			<v-autocomplete
				v-model="filters.billables"
				@change="getReport"
				:items="billables"
				label="Billable" item-value="id" item-text="code"
				outlined hide-details dense multiple
			/>
			<v-select
				v-model="filters.billableTypes"
				@change="getReport"
				:items="billableTypes"
				label="Billable Type" item-value="id" item-text="code"
				outlined hide-details dense multiple
			/>
			<q-user-autocomplete
				v-model="filters.users"
				@input="getReport"
				:items="users"
				label="User"
				outlined hide-details dense multiple
			/>
			<v-autocomplete
				v-model="filters.customers"
				@change="getReport"
				:items="customers"
				label="Customer" item-value="id" item-text="name"
				outlined hide-details dense multiple
			/>
			<v-select
				v-model="filters.byUser"
				@change="onByUserChange"
				:items="reportViews"
				item-value="value"
				item-text="text"
				label="View"
				class="billing-filter-view"
				outlined hide-details dense single-line
			/>
			<v-select
				v-model="selectedSections"
				@change="onSectionsChange"
				:items="sectionOptions"
				item-value="value"
				item-text="text"
				label="Sections"
				class="billing-filter-view billing-filter-sections"
				outlined hide-details dense multiple single-line
			>
				<template #selection="{ index }">
					<span
						v-if="index === 0"
						class="sections-selection-text"
						:title="sectionsSelectionText"
					>{{ sectionsSelectionText }}</span>
				</template>
			</v-select>
			<v-checkbox
				v-if="!filters.byUser"
				v-model="filters.includeWithoutBillable"
				@change="getReport"
				label="Without Billable"
				color="primary"
				class="mt-0 pt-0"
				style="height: 24px"
				hide-details
			/>
			<v-checkbox
				v-model="filters.includeBillablesOnZero"
				@change="getReport"
				label="Billables on Zero"
				color="primary"
				class="mt-0 pt-0"
				style="height: 24px"
				hide-details
			/>
		</div>

		<div class="chart new-back mt-2 bordered rounded-lg lighten" ref="chart">
			<table :class="{ 'fix-totals': fixTotals, 'by-user': filters.byUser }">
				<tbody>
					<tr class="sticky-header colored-header">
						<th v-if="!filters.byUser" rowspan="3" class="td-nb-x td-bb" style="background: var(--v-newDesignBackground-base) !important;">
							<div>
								Billable
							</div>
						</th>
						<th v-if="!filters.byUser" rowspan="3" class="td-nb-x td-bb" style="background: var(--v-newDesignBackground-base) !important;">
							<div class="fill-w">
								Type
							</div>
						</th>
						<th rowspan="3" class="td-nb-x td-bb" :class="{ 'td-nb-l': filters.byUser }" style="background: var(--v-newDesignBackground-base) !important;">
							<div>
								BlueTag
							</div>
						</th>
						<th
							v-for="(date, ix) in dates" :key="ix"
							:colspan="colsPerPeriod" scope="colgroup"
							class="td-nb-y" :class="{'fixed-total-sticky': date.isTotal && fixTotals}"
							:style="date.isTotal && fixTotals ? fixedTotalStyle(0) : null"
						>
							<div v-if="date.isTotal" class="relative">
								Totals
								<v-btn @click="fixTotals = !fixTotals" style="position: absolute; right: 10px" x-small icon>
									<v-icon>{{ fixTotals ? 'mdi-pin-off-outline' : 'mdi-pin-outline' }}</v-icon>
								</v-btn>
							</div>
							<span v-else>
								{{ date.format('MMM YYYY') }}
							</span>
						</th>
					</tr>
					<tr>
						<template v-for="(date, ix) in dates">
							<th
								v-for="group in sectionHeaderGroups"
								:key="ix + '-' + group.section"
								scope="col"
								:colspan="group.colspan"
								:class="[sectionGroupClass(group), date.isTotal && fixTotals ? 'fixed-total-sticky' : '']"
								:style="date.isTotal && fixTotals ? fixedTotalStyle(group.startColIndex) : null"
							>
								{{ group.text }}
							</th>
						</template>
					</tr>
					<tr class="colored-header">
						<template v-for="(date, ix) in dates">
							<th
								v-for="(col, cix) in visibleColumns"
								:key="ix + col.key"
								scope="col"
								:class="[columnHeaderClass(col, cix, ix), date.isTotal && fixTotals ? 'fixed-total-sticky' : '']"
								:style="date.isTotal && fixTotals ? fixedTotalStyle(cix) : null"
							>
								{{ col.label }}
							</th>
						</template>
					</tr>
					<template v-for="line in shownLines">
						<tr
							@click="doHighlight(line.key)"
							:key="line.key + 'a'"
							class="sticky-header customer-header"
							:class="{ 'highlighted': line.key == selectedId }"
						>
							<th v-if="!filters.byUser" class="td-nb-x">
							<div class="nobr text-center col-billable">{{ line.billableCode }}</div>
						</th>
						<th v-if="!filters.byUser" class="td-nb-x">
							<div class="text-center col-type">
								{{ line.billableType !== undefined ? billableTypes[line.billableType].code : '-' }}
							</div>
						</th>
						<th class="td-nb-l">
							<div class="text-center col-bluetag">{{ line.userBlueTag }}</div>
						</th>
	
							<template v-for="(period, ix) in line.periods">
								<td
									v-for="(col, cix) in visibleColumns"
									:key="ix + col.key"
									class="quantity-cell-customer"
									:class="[
										period.isTotal && fixTotals ? 'fixed-total-sticky' : '',
										col.unclassified && period.executedUnclassifiedHours > 0 ? 'unclassified-hit' : '',
									]"
									:style="period.isTotal && fixTotals ? fixedTotalStyle(cix) : null"
								>
									<div :class="hoursClass(period[col.field])">{{ period[col.field] | number(2, 0, true) }}</div>
								</td>
							</template>
						</tr>
					</template>
					<template v-for="line in billableTypeTotals">
						<tr
							@click="doHighlight(line.key)"
							:key="line.key + 'a'"
							class="sticky-header customer-header billable-totals"
							:class="{ 'highlighted': line.key == selectedId }"
						>
							<th v-if="!filters.byUser" class="td-nb-x">
							<div class="nobr text-center col-billable"></div>
						</th>
						<th v-if="!filters.byUser" class="td-nb-x">
							<div class="text-center col-type"></div>
						</th>
						<th class="td-nb-l">
							<div class="text-center col-bluetag">{{ filters.byUser ? (line.userBlueTag || 'Total') : billableTypes[line.billableType].code }}</div>
						</th>
	
							<template v-for="(period, ix) in line.periods">
								<td
									v-for="(col, cix) in visibleColumns"
									:key="ix + col.key"
									class="quantity-cell-customer"
									:class="[
										period.isTotal && fixTotals ? 'fixed-total-sticky' : '',
										col.unclassified && period.executedUnclassifiedHours > 0 ? 'unclassified-hit' : '',
									]"
									:style="period.isTotal && fixTotals ? fixedTotalStyle(cix) : null"
								>
									<div :class="hoursClass(period[col.field])">{{ period[col.field] | number(2, 0, true) }}</div>
								</td>
							</template>
						</tr>
					</template>
				</tbody>
			</table>
			<div class="pagination-container d-flex align-center">
				<div
					v-if="showUnclassifiedTotal"
					class="unclassified-total ml-2"
					title="Total unclassified hours"
				>
					{{ totalUnclassifiedHours | number(2, 0, true) }}
				</div>
				<div class="d-flex justify-end py-2 ml-auto">
					<div class="d-flex align-center mr-5 gap-1 nobr">
						Page: <b>{{ pagination.page }} / {{ pagination.pages }}</b>
					</div>
					<div class="d-flex align-center mr-5 gap-1 nobr">
						Rows per page:
						<v-select v-model="pagination.pageSize" :items="[5, 10, 20, 'All']" dense hide-details
							style="width: 50px" class="caption" />
					</div>
					<div style="position: relative">
						<v-pagination
							v-model="pagination.page"
							:length="pagination.pages"
							:total-visible="0"
						/>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import moment from "moment"

const COL_BILLABLE = 112
const COL_TYPE = 42
const COL_BLUETAG = 96
const COL_TOTAL = 70
const COL_SEAM = 1

const COLUMN_DEFS = [
	{ key: 'a', field: 'assignedBillableHours', section: 'assigned', label: 'Billable' },
	{ key: 'b', field: 'assignedNonBillableHours', section: 'assigned', label: 'Non Billable' },
	{ key: 'c', field: 'executedBillableHours', section: 'executed', label: 'Billable' },
	{ key: 'd', field: 'executedNonBillableHours', section: 'executed', label: 'Non Billable' },
	{ key: 'e', field: 'executedUnclassifiedHours', section: 'executed', label: 'Unclassified', unclassified: true },
	{ key: 'f', field: 'billedHours', section: 'billed', label: 'Billed' },
]

const SECTION_ORDER = ['assigned', 'executed', 'billed']

export default {
	title: 'Billing Report',
	data: () => ({
		selectedId: null,
		report: [],
		billableTypeTotals: [],
		filters: {
			startDate: moment().startOf('year').format('YYYY-MM-DD'),
			endDate: moment().endOf('year').format('YYYY-MM-DD'),
			billables: [],
			billableTypes: [],
			users: [],
			customers: [],
			includeWithoutBillable: false,
			includeBillablesOnZero: false,
			byUser: false,
		},
		pagination: {
			page: 1,
			pageSize: 20,
			pages: 1,
		},
		billables: [],
		billableTypes: [],
		users: [],
		customers: [],
		reportViews: [
			{ value: false, text: 'By Billable' },
			{ value: true, text: 'By User' },
		],
		sectionOptions: [
			{ value: 'assigned', text: 'Assigned' },
			{ value: 'executed', text: 'Executed' },
			{ value: 'billed', text: 'Billed' },
		],
		selectedSections: ['assigned', 'executed', 'billed'],

		fixTotals: false,
	}),
	created() {
		this.$q.log(4, 54, 3, 'NA')

		this.$http.get('api/enums/admin/billable/type')
			.then(res => {
				this.billableTypes = res.data
			})
			.catch(error => {
				console.error(error)
				this.$root.error('Failed to fetch billable types')
			})
		this.$http.get('api/billable')
			.then(res => {
				this.billables = res.data
			})
			.catch(error => {
				console.error(error)
				this.$root.error('Failed to fetch billables')
			})
		this.$http.get('api/user?onlyInterns=true')
			.then(res => {
				this.users = res.data
			})
			.catch(error => {
				console.error(error)
				this.$root.error('Failed to fetch users')
			})
		this.$http.get('api/customer')
			.then(res => {
				this.customers = res.data
			})
			.catch(error => {
				console.error(error)
				this.$root.error('Failed to fetch customers')
			})

		this.getReport()
	},
	computed: {
		shownLines() {
			let size = this.pagination.pageSize

			if(size == 'All') size = this.report.length

			let start = (this.pagination.page - 1) * size
			let end = start + size
			return this.report.slice(start, end)
		},
		dates() {
			if (!this.report.length) return []

			return this.report[0].periods
				.map(x => {
					let m = moment(x.period)
					m.isTotal = x.isTotal
					return m
				})
		},
		visibleSections() {
			return SECTION_ORDER.filter(section => this.selectedSections.includes(section))
		},
		visibleColumns() {
			const cols = COLUMN_DEFS.filter(col => this.selectedSections.includes(col.section))
			return cols.map((col, index) => ({
				...col,
				isLast: index === cols.length - 1,
				isLastInSection: index === cols.length - 1 || cols[index + 1].section !== col.section,
			}))
		},
		colsPerPeriod() {
			return this.visibleColumns.length
		},
		sectionHeaderGroups() {
			let startColIndex = 0
			return this.visibleSections.map((section, index) => {
				const columns = this.visibleColumns.filter(col => col.section === section)
				const group = {
					section,
					text: this.sectionOptions.find(x => x.value === section).text,
					colspan: columns.length,
					startColIndex,
					isLastGroup: index === this.visibleSections.length - 1,
				}
				startColIndex += columns.length
				return group
			})
		},
		frozenWidth() {
			return this.filters.byUser
				? COL_BLUETAG
				: COL_BILLABLE + COL_TYPE + COL_BLUETAG
		},
		totalsStart() {
			return this.frozenWidth + COL_SEAM
		},
		showUnclassifiedTotal() {
			return this.selectedSections.includes('executed') && this.totalUnclassifiedHours > 0
		},
		sectionsSelectionText() {
			return SECTION_ORDER
				.filter(section => this.selectedSections.includes(section))
				.map(section => this.sectionOptions.find(x => x.value === section).text)
				.join(', ')
		},
		unclassifiedCols() {
			if (!this.selectedSections.includes('executed')) return {}

			const cols = {}
			const scan = lines => {
				(lines || []).forEach(line => {
					(line.periods || []).forEach((period, ix) => {
						if ((period.executedUnclassifiedHours || 0) > 0) cols[ix] = true
					})
				})
			}
			scan(this.shownLines)
			scan(this.billableTypeTotals)
			return cols
		},
		totalUnclassifiedHours() {
			let total = 0
			this.report.forEach(line => {
				(line.periods || []).forEach(period => {
					if (!period.isTotal) total += period.executedUnclassifiedHours || 0
				})
			})
			return total
		},
	},
	methods: {
		getReport() {
			this.$http.post('api/admin/billingReport', this.filters)
				.then(res => {
					this.report = res.data.lines
					this.billableTypeTotals = res.data.billableTypeTotals

					if(this.pagination.pageSize == 'All')
						this.pagination.pages = 1
					else
						this.pagination.pages = Math.ceil(this.report.length / this.pagination.pageSize)
					this.pagination.page = 1
				})
				.catch(err => {
					console.error(err)
					this.$root.error('Error getting report')
				})
		},
		doHighlight(id){
			if(id == this.selectedId)
				this.selectedId = null
			else
				this.selectedId = id
		},
		onByUserChange(){
			if(this.filters.byUser)
				this.filters.includeWithoutBillable = false
			this.getReport()
		},
		onSectionsChange(){
			if(!this.selectedSections.length)
				this.selectedSections = SECTION_ORDER.slice()
		},
		fixedTotalStyle(colIndex){
			const left = colIndex <= 0
				? this.totalsStart
				: this.totalsStart + COL_TOTAL * colIndex + COL_SEAM
			return { left: `${left}px` }
		},
		sectionGroupClass(group){
			const classes = ['td-nb-y']
			if(group.section === 'assigned') classes.push('td-nb-r', 'subheader-assigned')
			else if(group.section === 'executed') classes.push('td-b-x', 'subheader-executed')
			else classes.push('td-nb-x')
			if(group.isLastGroup && group.section !== 'assigned') classes.push('td-nb-l')
			return classes
		},
		columnHeaderClass(col, colIndex, periodIndex){
			const classes = ['td-nb-y']
			if(col.isLast) classes.push('td-nb-l')
			else if(col.isLastInSection) classes.push('td-nb-r')
			else classes.push('td-nb-x')
			if(col.unclassified && this.unclassifiedCols[periodIndex]) classes.push('unclassified-title')
			return classes
		},
		hoursClass(value){
			const intDigits = Math.floor(Math.abs(Number(value) || 0)).toString().length
			if(intDigits > 6) return 'cell-xxs'
			if(intDigits > 4) return 'cell-xs'
			return ''
		},
	},
	watch: {
		'pagination.pageSize'(v){
			if(v == 'All'){
				this.pagination.pages = 1
			}
			else {
				this.pagination.pages = Math.ceil(this.report.length / this.pagination.pageSize)
			}
		}
	}
}
</script>

<style lang="scss" scoped>
$col-billable: 112px;
$col-type: 42px;
$col-bluetag: 96px;
$col-total: 70px;
$col-seam: 1px;

.col-billable { width: $col-billable; font-weight: 400; }
.col-type { width: $col-type; font-weight: 400; }
.col-bluetag { width: $col-bluetag; font-weight: 400; }

.subheader-assigned { background: #fdebd8 !important; }
.subheader-executed { background: #f0eafa !important; }

.unclassified-hit { background: #fbe3e3 !important; }

.highlighted {
	background-color: #c4dcec !important;
	td, th {
		background-color: #c4dcec !important;
	}
}

tr.sticky-header{
	th:nth-child(1),
	th:nth-child(2),
	th:nth-child(3),
	td:nth-child(1),
	td:nth-child(3),
	td:nth-child(2) {
		position: sticky;
	}
	&:first-child {
		th:nth-child(1),
		th:nth-child(2),
		th:nth-child(3) {
			z-index: 3;
		}
	}
	th:nth-child(1), td:nth-child(1) {
		left: 0;
	}
	th:nth-child(2), td:nth-child(2) {
		left: $col-billable;
	}
	th:nth-child(3), td:nth-child(3), td.third {
		left: $col-billable + $col-type;

		&::after {
			content: "";
			position: absolute;
			right: -1px; top: 0;
			height: 100%;
			width: 1px;
			background: var(--v-lightgray-lighten);
		}
	}
}

table.by-user {
	tr.sticky-header {
		th:nth-child(2), td:nth-child(2),
		th:nth-child(3), td:nth-child(3) {
			position: static;
			left: auto;
			&::after { display: none; }
		}
		th:nth-child(1), td:nth-child(1) {
			&::after {
				content: "";
				position: absolute;
				right: -1px; top: 0;
				height: 100%;
				width: 1px;
				background: var(--v-lightgray-lighten);
			}
		}
	}
}

.fixed-total-sticky {
	position: sticky;
	z-index: 3;
	border-right: none !important;
	border-left: none !important;
	&::after {
		content: "";
		position: absolute;
		bottom: 0; right: 0;
		width: 1px;
		height: 100%;
		background: var(--v-lightgray-lighten);
	}
}

table, th, td {
	border: 1px solid var(--v-lightgray-lighten);
  border-collapse: collapse;
	background: var(--v-newDesignBackground-base);
}

table tr{
	background: var(--v-newDesignBackground-base);
	&.colored-header{
		border-top: 1px solid var(--v-lightgray-lighten);
		th:nth-child(odd) {
			background: var(--v-coloredBack-base) !important;
		}
		th {
			width: $col-total;
		}
		th.unclassified-title {
			background: #fbe3e3 !important;
			color: #b3261e;
		}
	}
}

tbody {
	tr:first-child, tr:nth-child(2), tr:nth-child(3), .customer-header {
		position: sticky;
	}
	tr:first-child {
		top: -1px;
		&::after {
			content: "";
			position: absolute;
			bottom: -1px; left: 0;
			width: 100%;
			height: 1px;
			background: var(--v-lightgray-lighten);
		}
		z-index: 2;
	}
	tr:nth-child(2) {
		top: 20px;
		z-index: 1;
		&::after {
			content: "";
			position: absolute;
			bottom: -1px; left: 0;
			width: 100%;
			height: 1px;
			background: var(--v-lightgray-lighten);
		}
	}
	tr:nth-child(3) {
		top: 40px;
		z-index: 1;
		&::after {
			content: "";
			position: absolute;
			bottom: -1px; left: 0;
			width: 100%;
			height: 1px;
			background: var(--v-lightgray-lighten);
		}
	}
	.customer-header {
		top: 58px;
		z-index: 2;

		&::after {
			content: "";
			position: absolute;
			top: 1px; left: 0;
			width: 100%; height: calc(100% - 1px);
			background: transparent;
			border-top: 1px solid var(--v-lightgray-lighten);
			border-bottom: 1px solid var(--v-lightgray-lighten);
			pointer-events: none;
		}
	}
	.billable-totals {
		th, td {
			background: var(--v-background-lighten3);
		}
	}
}

tfoot {
	border-top: 1px solid var(--v-lightgray-lighten);
}

tr:not(.highlighted, .not-hover):hover {
	background: var(--v-lightgray-lighten2);

	> th, > td {
		background: var(--v-lightgray-lighten2) !important;
	}
}

.td-nb {
	border: none !important;
}
.td-nb-y {
	border-top: none !important;
	border-bottom: none !important;
}
.td-nb-x {
	border-left: none !important;
	border-right: none !important;
}
.td-nb-l {
	border-left: none !important;
}
.td-nb-r {
	border-right: none !important;
}
.td-br {
	border-right: 1px solid var(--v-lightgray-lighten) !important;
}
.td-b-x {
	border-left: 1px solid var(--v-lightgray-lighten) !important;
	border-right: 1px solid var(--v-lightgray-lighten) !important;
}
.td-bb::before{
	content: "";
	position: absolute;
	bottom: -1px; left: 0;
	width: 100%;
	height: 1px;
	background: var(--v-lightgray-lighten);
}

tr > td.sticky,
tr > th.sticky {
	position: sticky !important;
	left: 0;
	z-index: 1;
	background: var(--v-newDesignBackground-base);
  border-collapse: separate;
	border-spacing: 0;
}

.quantity-cell-customer {
	text-align: center;
	height: 28px;
	padding: 0 1px;

	> div {
		width: $col-total;
		height: 28px;
		line-height: 28px;
		font-size: 1.15em;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;

		&.cell-xs {
			font-size: 0.98em;
		}

		&.cell-xxs {
			font-size: 0.82em;
		}
	}
}

.chart::-webkit-scrollbar {
	height: 8px;
	background: var(--v-newDesignBackground-base);
	border-radius: 15px;
}

.chart::-webkit-scrollbar-thumb {
	background: var(--v-background-lighten2);
	border: 4px solid transparent;
	border-radius: 8px;

	&:hover {
		background: var(--v-coloredBack-base);
	}
}

.chart {
	max-height: calc(100vh - 20px);
	width: calc(100vw - 190px);
	overflow: scroll;
	font-size: .72em;
}

.billing-filter-view {
	flex: 0 0 auto;
	width: 219px;
	max-width: 219px;
}

.billing-filter-sections {
	::v-deep .v-select__selections {
		flex-wrap: nowrap;
		overflow: hidden;
	}

	.sections-selection-text {
		display: block;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		max-width: 100%;
	}
}

.header {
	position: sticky;
	top: 0;
	z-index: 2;
}

.customer-header-cont {
	padding: 10px 10px;

	.customer-header {
		padding: 5px 5px 5px 10px !important;
	}
}

.line {
	display: flex;
	width: max-content;
	width: 100%;
	background: var(--v-newDesignBackground-base);

	&:hover {
		background: var(--v-lightgray-lighten2);
	}

	.sticky {
		display: flex;
		position: sticky;
		align-items: flex-end;
		background: inherit;
		left: 0;
		z-index: 1;
		border-right: 2px solid var(--v-lightgray-lighten);
		overflow: hidden;
		width: 325px;

		.st {
			display: flex;
			align-items: center;
			padding: 10px 5px 10px 10px;

			div {
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}
		}

		.head {
			border-bottom: 2px solid var(--v-lightgray-lighten);
		}
	}

	.date {
		width: 670px;
		padding-top: 5px;
		border: 1px solid var(--v-lightgray-lighten);
		background: var(--v-newDesignBackground-base);
		border-bottom-width: 2px;
		border-top: 0;

		&.no-border {
			border-left: none !important;
		}

		.head {
			font-size: 1.2em;
		}
	}

	.period-grid {
		display: grid;
		grid-template-columns: repeat(6, 1fr);
		width: 670px;

		::v-deep>* {
			padding-right: 5px !important;
		}
	}
}

.data-container {
	.customer-lines {
		border-bottom: 1px solid var(--v-lightgray-lighten);

		.line {
			contain: paint;
		}
	}
}

.pagination-container {
	position: sticky;
	left: 0px;
}

.unclassified-total {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 34px;
	height: 34px;
	padding: 0 8px;
	border-radius: 999px;
	background: #e53935;
	color: #fff;
	font-weight: 600;
	font-size: 0.8rem;
	line-height: 1;
}

.non-billable{
	font-style: italic;
}

::v-deep .v-pagination__navigation {
	box-shadow: none !important;
	background: transparent !important;
}
</style>